import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import adminSchemaApi from '../api/adminSchemaApi';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';

const DEFAULT_CONFIG = {
  tab: {
    maxTabs: 5,
    allowCreate: true,
  },
  field: {
    maxCustom: 50,
    allowCreate: false,
    allowStandardEdit: true,
    allowStandardDelete: false,
  },
  layout: {
    countMode: 'pair',
    maxLayouts: 1,
    allowCreate: true,
  },
  migration: {
    allow: false,
  },
  objectManager: {
    mode: 'allowlist',
    scope: 'standard',
    allow_create: true,
    enable_standard: true,
    allowedObjects: ['account', 'contact', 'opportunity', 'lead', 'order'],
  },
  leadConversionMapping: {
    mode: 'allowlist',
    allow: true,
    allowCustomObject: true,
    scope: 'standard',
    maxObjects: 5,
    allowedObjects: ['account', 'contact', 'opportunity', 'lead', 'order'],
  },
  setup: {
    mode: 'allowlist',
    allowedItems: [
      'objectmanager',
      'rolemanager',
      'tabs',
      'appmanager',
      'actions',
      'workflows',
      'homewidget',
      'webforms',
      'scoringrule',
      'usergroups',
      'users',
      'mailconfig',
      'emailtemplate',
      'assignmentrule',
      'searchviews',
      'session_setting',
      'stage_master',
    ],
  },
};

const normalizeConfig = config => {
  const merged = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
  if (config && typeof config === 'object') {
    Object.keys(merged).forEach(section => {
      if (config[section] && typeof config[section] === 'object') {
        merged[section] = { ...merged[section], ...config[section] };
      }
    });
  }
  return merged;
};

const AdminSchemaConfig = () => {
  const { tenantCode } = useParams();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [availableStandardObjects, setAvailableStandardObjects] = useState([]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const [data, objects] = await Promise.all([
          adminSchemaApi.fetchConfig(tenantCode),
          adminSchemaApi.fetchObjects(tenantCode),
        ]);
        const normalized = normalizeConfig(data);
        setConfig(normalized);
        setOriginalConfig(normalized);
        const allObjects = Array.isArray(objects)
          ? objects.map(obj => ({
            name: String(obj.object_name || '').toLowerCase(),
            label: obj.object_label || obj.object_name,
            isStandard: !!obj.is_standard,
            enableLeadMapping: !!obj.enable_lead_mapping,
          }))
          : [];
        setAvailableObjects(allObjects);
        setAvailableStandardObjects(
          allObjects.filter(obj => obj.isStandard)
        );
      } catch (err) {
        setError('Failed to load config.');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [tenantCode]);

  const handleSave = async () => {
    setError('');
    setMessage('');
    try {
      setSaving(true);
      const success = await adminSchemaApi.saveConfig(tenantCode, config);
      if (success) {
        setMessage('Config saved.');
        setOriginalConfig(config);
        setEditMode(false);
      } else {
        setError('Failed to save config.');
      }
    } catch (err) {
      setError('Failed to save config.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setConfig(originalConfig);
    setEditMode(false);
    setMessage('');
    setError('');
  };

  const handleAllowedObjectsChange = (section, values) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        allowedObjects: values,
      },
    }));
  };

  const isReadOnly = !editMode || loading;

  const objectManagerAllowedCount = useMemo(
    () => config.objectManager.allowedObjects.length,
    [config.objectManager.allowedObjects]
  );

  const conversionAllowedCount = useMemo(
    () => config.leadConversionMapping.allowedObjects.length,
    [config.leadConversionMapping.allowedObjects]
  );

  const standardOptions = useMemo(
    () =>
      availableStandardObjects.length
        ? availableStandardObjects
        : [
          { name: 'account', label: 'Account', isStandard: true },
          { name: 'contact', label: 'Contact', isStandard: true },
          { name: 'opportunity', label: 'Opportunity', isStandard: true },
          { name: 'lead', label: 'Lead', isStandard: true },
          { name: 'order', label: 'Order', isStandard: true },
        ],
    [availableStandardObjects]
  );

  const allOptions = useMemo(
    () =>
      availableObjects.length
        ? availableObjects
        : [
          { name: 'account', label: 'Account', isStandard: true },
          { name: 'contact', label: 'Contact', isStandard: true },
          { name: 'opportunity', label: 'Opportunity', isStandard: true },
          { name: 'lead', label: 'Lead', isStandard: true },
          { name: 'order', label: 'Order', isStandard: true },
        ],
    [availableObjects]
  );

  const standardPicklistOptions = useMemo(
    () =>
      standardOptions.map(option => ({
        key: option.name,
        value: option.label,
      })),
    [standardOptions]
  );

  // Lead conversion should show core objects always.
  // Custom objects are shown only when backend marks them as lead-mapping-enabled.
  const leadConversionPicklistOptions = useMemo(() => {
    const core = new Set(['account', 'contact', 'opportunity']);
    const base = allOptions
      .filter(option => {
        const objectName = String(option.name || '').toLowerCase();
        if (core.has(objectName)) return true;
        if (option.isStandard === false) return option.enableLeadMapping === true;
        return false;
      })
      .map(option => ({ key: option.name, value: option.label }));
    const baseKeys = new Set(base.map(option => String(option.key).toLowerCase()));
    const selectedExtras = (config?.leadConversionMapping?.allowedObjects || [])
      .filter(name => name && !baseKeys.has(String(name).toLowerCase()))
      .map(name => ({
        key: name,
        value: String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      }));
    return selectedExtras.length > 0 ? [...base, ...selectedExtras] : base;
  }, [allOptions, config?.leadConversionMapping?.allowedObjects]);

  const setupOptions = useMemo(
    () => [
      { key: 'objectmanager', value: 'Object Manager' },
      { key: 'rolemanager', value: 'Role Manager' },
      { key: 'tabs', value: 'Tab Manager' },
      { key: 'appmanager', value: 'App Manager' },
      { key: 'actions', value: 'Action Manager' },
      { key: 'workflows', value: 'Workflow Manager' },
      { key: 'homewidget', value: 'Edit Home' },
      { key: 'webforms', value: 'Web Forms' },
      { key: 'scoringrule', value: 'Scoring Rules' },
      { key: 'usergroups', value: 'User Groups' },
      { key: 'users', value: 'Users' },
      { key: 'mailconfig', value: 'Mail Config' },
      { key: 'emailtemplate', value: 'Email Template' },
      { key: 'assignmentrule', value: 'Assignment Rules' },
      { key: 'searchviews', value: 'Search Views' },
      { key: 'session_setting', value: 'Session Settings' },
      { key: 'stage_master', value: 'Probability Manager' },
    ],
    []
  );

  const renderActionButtons = () => (
    <div className='d-flex gap-2'>
      {editMode ? (
        <>
          <Button
            variant='primary'
            className='admin-primary-btn'
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant='secondary' onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
        </>
      ) : (
        <Button
          variant='primary'
          className='admin-primary-btn'
          onClick={() => setEditMode(true)}
          disabled={loading || saving}
        >
          Edit
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <div>
          <h2 className='admin-title mb-1'>Schema Config</h2>
          <p className='admin-subtitle mb-0'>Tenant: {tenantCode}</p>
        </div>
        {renderActionButtons()}
      </div>

      {error && <Alert variant='danger'>{error}</Alert>}
      {message && <Alert variant='success'>{message}</Alert>}

      {loading ? (
        <div className='admin-subtitle'>Loading...</div>
      ) : (
        <>
          <Row className='g-3'>
            <Col md={6}>
              <Card className='p-3'>
                <h5>Tab Configuration</h5>
                <Form.Group className='mb-2'>
                  <Form.Label>Max Tabs</Form.Label>
                  <Form.Control
                    type='number'
                    value={config.tab.maxTabs}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        tab: {
                          ...prev.tab,
                          maxTabs: Number(event.target.value) || 0,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  />
                </Form.Group>
                <Form.Check
                  type='switch'
                  label='Allow Create'
                  checked={config.tab.allowCreate}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      tab: { ...prev.tab, allowCreate: event.target.checked },
                    }))
                  }
                  disabled={isReadOnly}
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Field Management</h5>
                <Form.Group className='mb-2'>
                  <Form.Label>Max Custom Fields</Form.Label>
                  <Form.Control
                    type='number'
                    value={config.field.maxCustom}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        field: {
                          ...prev.field,
                          maxCustom: Number(event.target.value) || 0,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  />
                </Form.Group>
                <Form.Check
                  type='switch'
                  label='Allow Create'
                  checked={config.field.allowCreate}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      field: {
                        ...prev.field,
                        allowCreate: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
                <Form.Check
                  type='switch'
                  label='Allow Standard Edit'
                  checked={config.field.allowStandardEdit}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      field: {
                        ...prev.field,
                        allowStandardEdit: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
                <Form.Check
                  type='switch'
                  label='Allow Standard Delete'
                  checked={config.field.allowStandardDelete}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      field: {
                        ...prev.field,
                        allowStandardDelete: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Layout Settings</h5>
                <Form.Group className='mb-2'>
                  <Form.Label>Count Mode</Form.Label>
                  <Form.Select
                    value={config.layout.countMode}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        layout: {
                          ...prev.layout,
                          countMode: event.target.value,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  >
                    <option value='pair'>pair</option>
                    <option value='single'>single</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className='mb-2'>
                  <Form.Label>Max Layouts</Form.Label>
                  <Form.Control
                    type='number'
                    value={config.layout.maxLayouts}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        layout: {
                          ...prev.layout,
                          maxLayouts: Number(event.target.value) || 0,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  />
                </Form.Group>
                <Form.Check
                  type='switch'
                  label='Allow Create'
                  checked={config.layout.allowCreate}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      layout: {
                        ...prev.layout,
                        allowCreate: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Object Manager</h5>
                <Form.Check
                  type='switch'
                  label='Allow Create'
                  checked={!!config.objectManager.allow_create}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      objectManager: {
                        ...prev.objectManager,
                        allow_create: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                  className='mb-2'
                />
                <Form.Check
                  type='switch'
                  label='Enable Standard'
                  checked={!!config.objectManager.enable_standard}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      objectManager: {
                        ...prev.objectManager,
                        enable_standard: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                  className='mb-2'
                />
                <div className='mb-2 admin-subtitle'>
                  Allowed Objects ({objectManagerAllowedCount})
                </div>
                <MultiSelectDropdown
                  selectedValues={config.objectManager.allowedObjects}
                  picklistValues={standardPicklistOptions}
                  onChange={values =>
                    handleAllowedObjectsChange('objectManager', values)
                  }
                  disabled={isReadOnly}
                  placeholder='Select objects'
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Lead Conversion Mapping</h5>
                <Form.Check
                  type='switch'
                  label='Allow Mapping'
                  checked={config.leadConversionMapping.allow}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      leadConversionMapping: {
                        ...prev.leadConversionMapping,
                        allow: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
                <Form.Check
                  type='switch'
                  label='Allow Custom Objects'
                  checked={!!config.leadConversionMapping.allowCustomObject}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      leadConversionMapping: {
                        ...prev.leadConversionMapping,
                        allowCustomObject: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
                <Form.Group className='mb-2 mt-2'>
                  <Form.Label>Mode</Form.Label>
                  <Form.Select
                    value={config.leadConversionMapping.mode}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        leadConversionMapping: {
                          ...prev.leadConversionMapping,
                          mode: event.target.value,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  >
                    <option value='allowlist'>allowlist</option>
                    <option value='denylist'>denylist</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className='mb-2'>
                  <Form.Label>Max Objects</Form.Label>
                  <Form.Control
                    type='number'
                    value={config.leadConversionMapping.maxObjects}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        leadConversionMapping: {
                          ...prev.leadConversionMapping,
                          maxObjects: Number(event.target.value) || 0,
                        },
                      }))
                    }
                    disabled={isReadOnly}
                  />
                </Form.Group>
                <div className='mb-2 admin-subtitle'>
                  Allowed Objects ({conversionAllowedCount})
                </div>
                <MultiSelectDropdown
                  selectedValues={config.leadConversionMapping.allowedObjects}
                  picklistValues={leadConversionPicklistOptions}
                  onChange={values =>
                    handleAllowedObjectsChange('leadConversionMapping', values)
                  }
                  disabled={isReadOnly}
                  placeholder='Select objects'
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Data Migration</h5>
                <Form.Check
                  type='switch'
                  label='Allow Migration'
                  checked={config.migration.allow}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      migration: {
                        ...prev.migration,
                        allow: event.target.checked,
                      },
                    }))
                  }
                  disabled={isReadOnly}
                />
              </Card>
            </Col>

            <Col md={6}>
              <Card className='p-3'>
                <h5>Setup Menu</h5>
                <Form.Group className='mb-2'>
                  <Form.Label>Mode</Form.Label>
                  <Form.Select
                    value={config.setup.mode}
                    onChange={event =>
                      setConfig(prev => ({
                        ...prev,
                        setup: { ...prev.setup, mode: event.target.value },
                      }))
                    }
                    disabled={isReadOnly}
                  >
                    <option value='allowlist'>allowlist</option>
                    <option value='denylist'>denylist</option>
                  </Form.Select>
                </Form.Group>
                <MultiSelectDropdown
                  selectedValues={config.setup.allowedItems}
                  picklistValues={setupOptions}
                  onChange={values =>
                    setConfig(prev => ({
                      ...prev,
                      setup: { ...prev.setup, allowedItems: values },
                    }))
                  }
                  disabled={isReadOnly}
                  placeholder='Select setup items'
                />
              </Card>
            </Col>
          </Row>

          <div className='mt-3 d-flex justify-content-end'>
            {renderActionButtons()}
          </div>
        </>
      )}
    </>
  );
};

export default AdminSchemaConfig;
